import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  type GroupStore,
  type IntakeSummary,
  type ItemStatus,
  type StoreStage,
  useGroupOnboarding,
  useIntakeLinkAction,
  useStoreGroupOptions,
} from '../../features/groupOnboarding/api';
import { relativeTime } from '../../shared';
import {
  Button,
  Callout,
  ConfirmDialog,
  CopyButton,
  FiguresSummary,
  GlyphLegend,
  HueGlyph,
  hueFor,
  IconPhone,
  IconPlus,
  IconStore,
  IconWarning,
  Listbox,
  Page,
  SectionTitleRow,
  StatusPill,
  StepCheck,
  StepNone,
  StepRing,
  StepWait,
  SummaryFigure,
  SummaryFigureSkeleton,
  Surface,
  Table,
  TableSkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
  TRGroup,
  type Tone,
} from '../../ui/v2';
import { AddLocationDialog } from './AddLocationDialog';

/**
 * Onboarding — `/onboarding`, for a group's owners, admins and managers.
 *
 * Every location's Getting started steps in one grid, the new locations
 * whose intake is still open, and "Add a location". It stands in for the
 * group dashboard until there is one. A user in more than one group picks
 * the group in the header.
 */

const COLUMNS: { key: string; label: string }[] = [
  { key: 'hours', label: 'Hours' },
  { key: 'documents', label: 'Docs' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'opentable', label: 'OpenTable' },
  { key: 'stripe', label: 'Stripe' },
  { key: 'test', label: 'Tested' },
  { key: 'ordering_access', label: 'Ordering access' },
  { key: 'transfer_line', label: 'Staff number' },
  { key: 'go_live', label: 'Forwarded' },
];
const SPAN = COLUMNS.length + 2;

const STAGE: Record<StoreStage, { label: string; tone: Tone }> = {
  live: { label: 'Live', tone: 'success' },
  ready: { label: 'Ready to go live', tone: 'accent' },
  in_setup: { label: 'In setup', tone: 'neutral' },
  not_started: { label: 'Not started', tone: 'neutral' },
};

const STATUS_WORD: Record<ItemStatus | 'none', string> = {
  done: 'done',
  todo: 'to do',
  locked: 'to do',
  waiting: 'waiting on Ask Arthur',
  recommended: 'recommended',
  none: 'not needed',
};

const Glyph: React.FC<{ status: ItemStatus | 'none' }> = ({ status }) => {
  if (status === 'done')
    return <StepCheck size={16} className="text-v2-success" />;
  if (status === 'waiting')
    return <StepWait size={16} className="text-v2-sky" />;
  if (status === 'none') return <StepNone size={16} className="text-v2-fg-4" />;
  return (
    <StepRing size={16} tone={status === 'recommended' ? 'muted' : 'danger'} />
  );
};

/** One letter in the location's own hue, so rows keep their colour. */
const LocationTile: React.FC<{ name: string; seed: string | number }> = ({
  name,
  seed,
}) => (
  <HueGlyph hue={hueFor(seed)} size="sm" className="text-v2-13 font-semibold">
    {name.charAt(0).toUpperCase() || '?'}
  </HueGlyph>
);

/** Quiet steps-done meter beside the pill of a location still in setup. */
const Readiness: React.FC<{ counts: GroupStore['counts'] }> = ({ counts }) => (
  <span className="inline-flex items-center gap-2">
    <span className="h-1 w-10 overflow-hidden rounded-full bg-v2-sunken">
      <span
        className="block h-full rounded-full bg-v2-accent"
        style={{
          width: `${counts.total ? Math.round((counts.done / counts.total) * 100) : 0}%`,
        }}
      />
    </span>
    <span className="v2-num whitespace-nowrap text-v2-12 text-v2-fg-3">
      {counts.done} of {counts.total}
    </span>
  </span>
);

/** Pinned so the location stays in view while the steps scroll. */
const PIN = 'sticky left-0 z-[1] whitespace-nowrap bg-v2-surface';
/** Step columns are narrow, their names wrap onto two lines. */
const STEP_TH = '!h-auto !whitespace-normal !px-1.5 py-2 leading-[14px]';

const StoreRow: React.FC<{ store: GroupStore; canOpen: boolean }> = ({
  store,
  canOpen,
}) => {
  const items = new Map(store.items.map((i) => [i.key, i.status]));
  const stage = STAGE[store.status];
  return (
    <TR>
      <TD className={PIN}>
        <span className="flex items-center gap-2.5">
          <LocationTile name={store.name} seed={store.id} />
          {canOpen ? (
            <button
              type="button"
              className="text-left font-medium text-v2-fg hover:underline"
              onClick={() => {
                // Every query is scoped by X-Store-ID; switching reloads.
                localStorage.setItem('currentStoreId', String(store.id));
                window.location.assign('/getting-started');
              }}
            >
              {store.name}
            </button>
          ) : (
            <span className="font-medium text-v2-fg">{store.name}</span>
          )}
        </span>
      </TD>
      <TD>
        <span className="flex items-center gap-2.5">
          <StatusPill tone={stage.tone}>{stage.label}</StatusPill>
          {store.status !== 'live' && <Readiness counts={store.counts} />}
        </span>
      </TD>
      {COLUMNS.map((c) => {
        const status = items.get(c.key) ?? 'none';
        return (
          <TD key={c.key} align="center" className="px-1.5">
            <span
              role="img"
              aria-label={`${c.label}: ${STATUS_WORD[status]}`}
              className="inline-flex"
            >
              <Glyph status={status} />
            </span>
          </TD>
        );
      })}
    </TR>
  );
};

const intakeStatus = (i: IntakeSummary) => {
  switch (i.state) {
    case 'approved':
      return { tone: 'success' as const, label: 'Approved' };
    case 'submitted':
      return { tone: 'accent' as const, label: 'Ask Arthur review' };
    case 'expired':
      return { tone: 'warning' as const, label: 'Link expired' };
    case 'revoked':
      return { tone: 'neutral' as const, label: 'Link turned off' };
    default:
      return i.first_opened_at
        ? {
            tone: 'neutral' as const,
            label: `${i.confirmed} of ${i.total} confirmed`,
          }
        : { tone: 'neutral' as const, label: 'Not opened' };
  }
};

const IntakeRow: React.FC<{
  intake: IntakeSummary;
  onRevoke: () => void;
  onNewLink: () => void;
  busy: boolean;
}> = ({ intake, onRevoke, onNewLink, busy }) => {
  const status = intakeStatus(intake);
  const details = [
    intake.copied_from_name ? `From ${intake.copied_from_name}` : 'Blank',
    intake.recipient_email ? `Sent to ${intake.recipient_email}` : null,
    intake.last_activity_at
      ? `Last activity ${relativeTime(intake.last_activity_at)}`
      : null,
  ].filter(Boolean);
  return (
    <TR>
      <TD className={PIN}>
        <span className="flex items-center gap-2.5">
          <LocationTile
            name={intake.restaurant_name || intake.location_name}
            seed={intake.restaurant_name || intake.location_name}
          />
          <span className="font-medium text-v2-fg">
            {intake.restaurant_name || intake.location_name}
          </span>
        </span>
      </TD>
      <TD>
        <StatusPill tone={status.tone} hollow={intake.state === 'open'}>
          {status.label}
        </StatusPill>
      </TD>
      <TD colSpan={COLUMNS.length}>
        <div className="flex items-center justify-between gap-4">
          <span className="truncate text-v2-12 text-v2-fg-3">
            {details.join(' · ')}
          </span>
          <span className="flex shrink-0 items-center gap-2">
            {intake.state === 'open' && (
              <>
                <CopyButton text={intake.url} size="sm" variant="secondary">
                  Copy link
                </CopyButton>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onRevoke}
                  disabled={busy}
                >
                  Turn off link
                </Button>
              </>
            )}
            {(intake.state === 'expired' || intake.state === 'revoked') && (
              <Button
                variant="secondary"
                size="sm"
                onClick={onNewLink}
                loading={busy}
              >
                New link
              </Button>
            )}
          </span>
        </div>
      </TD>
    </TR>
  );
};

const GroupOnboardingPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_platform_admin === true;
  // A platform admin may open any group; everyone else only their own.
  const allGroups = useStoreGroupOptions(isAdmin).data;
  const groups = (isAdmin && allGroups) || user?.onboarding_groups || [];
  const [params] = useSearchParams();
  // `/onboarding?group=…`, the admin intakes table's way in.
  const fromUrl = Number(params.get('group')) || null;
  const [picked, setPicked] = useState<string>('');
  const groupId =
    Number(picked) ||
    fromUrl ||
    // user-info gives an admin the selected store's group, and an admin may
    // open any group, so the anchor needn't wait for the list. A member's
    // anchor must be one of their own groups.
    (isAdmin
      ? (user?.store_group_id ?? null)
      : groups.find((g) => g.id === user?.store_group_id)?.id) ||
    // One group needs no choosing. An admin with several and no anchor
    // picks; a member's first group is their own.
    (isAdmin && groups.length > 1 ? null : groups[0]?.id) ||
    null;
  const query = useGroupOnboarding(groupId);
  const action = useIntakeLinkAction();
  const [adding, setAdding] = useState(false);
  const [revoking, setRevoking] = useState<IntakeSummary | null>(null);
  const data = query.data;
  const openable = new Set(
    user?.is_platform_admin
      ? (data?.stores ?? []).map((s) => s.id)
      : (user?.available_stores ?? []).map((s) => s.id),
  );
  const intakes = data?.intakes ?? [];
  const stores = data?.stores ?? [];
  const liveCount = stores.filter((s) => s.status === 'live').length;
  // A location needs the group when it is not live and has steps that are
  // the group's to do — all-waiting means the ball is with Ask Arthur.
  const needsAction = stores.filter(
    (s) => s.status !== 'live' && s.counts.todo > 0,
  );

  return (
    <Page
      fill
      title="Onboarding"
      meta={groups.length === 1 ? data?.group.name : undefined}
      actions={
        <>
          {groups.length > 1 && (
            <div className="w-[220px]">
              <Listbox
                aria-label="Group"
                size="sm"
                value={groupId ? String(groupId) : ''}
                onChange={setPicked}
                options={groups.map((g) => ({
                  value: String(g.id),
                  label: g.name,
                }))}
              />
            </div>
          )}
          <Button
            variant="primary"
            leading={<IconPlus size={14} />}
            onClick={() => setAdding(true)}
            disabledReason={
              groupId == null
                ? 'Choose a group first'
                : data
                  ? undefined
                  : 'Loading the group'
            }
          >
            Add a location
          </Button>
        </>
      }
    >
      {groupId == null ? (
        <Callout tone="neutral">
          {isAdmin
            ? 'Choose a group to see its onboarding.'
            : 'Onboarding is for the owners, admins and managers of a group.'}
        </Callout>
      ) : query.isError ? (
        <Callout tone="danger" title="Couldn’t load the locations">
          {query.error.message}{' '}
          <button
            type="button"
            className="underline"
            onClick={() => void query.refetch()}
          >
            Try again
          </button>
        </Callout>
      ) : (
        <>
          {/* columns: the loaded figures sit in one fragment child. */}
          <FiguresSummary aria-label="Rollout" aria-busy={!data} columns={3}>
            {!data ? (
              [0, 1, 2].map((i) => <SummaryFigureSkeleton key={i} icon />)
            ) : (
              <>
                <SummaryFigure
                  label="Rollout locations"
                  value={stores.length}
                  icon={<IconStore size={13} />}
                  secondary={
                    intakes.length > 0
                      ? `${intakes.length} more in intake`
                      : undefined
                  }
                />
                <SummaryFigure
                  label="Already live"
                  value={liveCount}
                  icon={<IconPhone size={13} />}
                  hue="teal"
                />
                <SummaryFigure
                  label="Needs action"
                  value={needsAction.length}
                  icon={<IconWarning size={13} />}
                  hue={needsAction.length > 0 ? 'amber' : 'neutral'}
                  secondary={
                    needsAction.length === 1
                      ? (needsAction[0].next_step?.title ?? needsAction[0].name)
                      : undefined
                  }
                />
              </>
            )}
          </FiguresSummary>
          <div>
            <SectionTitleRow
              title="Locations"
              count={data ? stores.length : undefined}
            />
            <Surface className="mt-2">
              <div className="v2-scroll-x overflow-x-auto">
                <Table>
                  <THead>
                    <tr>
                      <TH className={PIN}>Location</TH>
                      <TH>Status</TH>
                      {COLUMNS.map((c) => (
                        <TH
                          key={c.key}
                          align="center"
                          width={76}
                          className={STEP_TH}
                        >
                          {c.label}
                        </TH>
                      ))}
                    </tr>
                  </THead>
                  <TBody>
                    {!data ? (
                      <TableSkeletonRows rows={3} cols={SPAN} />
                    ) : (
                      <>
                        {data.stores.map((store) => (
                          <StoreRow
                            key={store.id}
                            store={store}
                            canOpen={openable.has(store.id)}
                          />
                        ))}
                        {intakes.length > 0 && (
                          <>
                            <TRGroup
                              colSpan={SPAN}
                              label="New locations"
                              count={intakes.length}
                            />
                            {intakes.map((intake) => (
                              <IntakeRow
                                key={intake.id}
                                intake={intake}
                                busy={
                                  action.isPending &&
                                  action.variables?.id === intake.id
                                }
                                onRevoke={() => setRevoking(intake)}
                                onNewLink={() =>
                                  action.mutate({
                                    id: intake.id,
                                    action: 'new-link',
                                  })
                                }
                              />
                            ))}
                          </>
                        )}
                      </>
                    )}
                  </TBody>
                </Table>
              </div>
            </Surface>
          </div>
          <GlyphLegend
            items={[
              {
                glyph: <StepCheck size={14} className="text-v2-success" />,
                label: 'Done',
              },
              { glyph: <StepRing size={14} tone="danger" />, label: 'To do' },
              {
                glyph: <StepWait size={14} className="text-v2-sky" />,
                label: 'Waiting on Ask Arthur',
              },
              {
                glyph: <StepNone size={14} className="text-v2-fg-4" />,
                label: 'Not needed',
              },
            ]}
          />
          {action.isError && (
            <Callout tone="danger">{action.error.message}</Callout>
          )}
        </>
      )}

      {adding && data && (
        <AddLocationDialog
          open
          onClose={() => setAdding(false)}
          groupId={data.group.id}
          stores={data.stores}
        />
      )}
      <ConfirmDialog
        open={revoking != null}
        onClose={() => setRevoking(null)}
        title={`Turn off ${revoking?.location_name ?? 'this location'}’s link?`}
        consequence="The link stops working. The answers are kept, and you can make a new link."
        confirmLabel="Turn off link"
        onConfirm={() => {
          if (revoking) action.mutate({ id: revoking.id, action: 'revoke' });
          setRevoking(null);
        }}
      />
    </Page>
  );
};

export default GroupOnboardingPage;
